import { Injectable, Optional } from '@angular/core';

export interface LocalStorageAccessor {
  get: (key: string, defaultValue?: any) => any;

  set: (key: string, value: any) => Promise<boolean>;

  has: (key: string) => any;

  remove: (key: string) => void;
}

export abstract class LocalStorageKeyBuilder {
  public abstract buildScopedKey(key: string): string;
}

@Injectable({ providedIn: 'root' })
export class LocalStorageService implements LocalStorageAccessor {
  // As we don't want to block UI when writing to localStorage, so here is small workaround.
  // in Chrome there might be slightly longer delay than in other browsers because of:
  // https://bugs.chromium.org/p/chromium/issues/detail?id=811451
  // but it shouldn't be big problem because we don't do something like read -> write -> read (and we shouldn't anyway :D)
  private readonly scheduler: (cb: Function) => void =
    typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : requestAnimationFrame;
  private readonly storage: Storage = localStorage;

  constructor(@Optional() private localStorageKeyBuilder?: LocalStorageKeyBuilder) {}

  public get(key: string, defaultValue: any = null): any {
    const _key = this.getKey(key);

    const value = this.getValue(_key);
    if (value !== null) {
      return value;
    }

    if (this.hasValue(key)) {
      return this.migrateOldKeys(key, _key);
    }

    return defaultValue;
  }

  public has(key: string): boolean {
    const _key = this.getKey(key);
    return this.hasValue(_key);
  }

  public set(key: string, value: any): Promise<boolean> {
    const _key = this.getKey(key);
    return this.saveValue(_key, value);
  }

  public remove(key: string): any {
    const _key = this.getKey(key);
    return this.deleteValue(_key);
  }

  private getKey(key: string) {
    if (!this.localStorageKeyBuilder) return `recruitee_${key}`;

    return this.localStorageKeyBuilder.buildScopedKey(key);
  }

  private deleteValue(key: string): any {
    return this.storage.removeItem(key);
  }

  private getValue(key: string): any {
    const raw = this.storage.getItem(key);
    try {
      return JSON.parse(raw!);
    } catch (err) {
      return raw;
    }
  }

  private hasValue(key: string): boolean {
    return this.storage.hasOwnProperty(key);
  }

  private saveValue(key: string, value: any): Promise<boolean> {
    return new Promise((res, rej) => {
      if (value !== undefined) {
        const storage = this.storage;
        this.scheduler.bind(window)(() => {
          try {
            storage.setItem(key, JSON.stringify(value));
          } catch (err) {
            const isQuotaExceeded = this.isQuotaExceededError(err);
            if (isQuotaExceeded) {
              storage.clear();
              storage.setItem(key, JSON.stringify(value));
            }
          }
          res(true);
        });
      } else {
        rej();
      }
    });
  }

  private migrateOldKeys(key: string, scopedKey: string): any {
    const unscopedValue = this.getValue(key);
    this.saveValue(scopedKey, unscopedValue);
    return unscopedValue;
  }

  private isQuotaExceededError(exception: DOMException): boolean {
    if (!exception || !exception.code) return false;

    const { code, name } = exception;
    if (code === 22) return true;
    if (code === 1014 && name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;

    return false;
  }
}
